// @@POS_REPORTS_v104@@ Client portal — POS sales on a lease (final days only) with the
// full settlement breakdown per the accepted terms, plus the settlement once issued.
// Data: GET /api/portal/leases/:id/pos (server-scoped to this login's clients).
// @@POS_TENDER_v104_1@@ card vs cash: cash stays with the client; tax is on all sales,
// the service fee on card sales; a report can cover one day or a date range.
import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

function money(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return (n < 0 ? '−$' : '$') + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  if (!m) return '—';
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
const R = { textAlign: 'right', whiteSpace: 'nowrap' };
const spanOf = (r) => (r.period_end ? `${fmtYmd(r.report_date)} – ${fmtYmd(r.period_end)}` : fmtYmd(r.report_date));
const V = { whiteSpace: 'nowrap' };
const LINE = { display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 14, padding: '3px 0' };

export default function LeasePosSales({ clientId, leaseId }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState('');
  useEffect(() => {
    let on = true;
    apiFetch(`/api/portal/leases/${leaseId}/pos?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : null)).then((x) => { if (on) setD(x || { reports: [] }); }).catch(() => on && setD({ reports: [] }));
    return () => { on = false; };
  }, [clientId, leaseId]);
  if (!d || (!d.reports.length && !d.settlement)) return null;
  const t = d.totals || {};
  const s = d.settlement;

  return (
    <div className="cp-card">
      <div className="cp-card-head" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h2>POS sales</h2>
        <span className="cp-sub" style={{ marginTop: 0 }}>{t.days} report{t.days === 1 ? '' : 's'} · due to you {money(t.amount_due)}</span>
      </div>
      <div className="cp-inv-scroll">
        <table className="cp-table">
          <thead><tr><th>Date</th><th style={R}>Net sales</th><th style={R}>Cash (kept)</th><th style={R}>Sales tax</th><th style={R}>Card fees</th><th style={R}>Service fee</th><th style={R}>Due to you</th></tr></thead>
          <tbody>
            {d.reports.map((r) => (
              <React.Fragment key={r.report_date}>
                <tr style={{ cursor: 'pointer' }} onClick={() => setOpen(open === r.report_date ? '' : r.report_date)} aria-expanded={open === r.report_date}>
                  <td style={{ whiteSpace: 'nowrap' }}>{spanOf(r)}{r.tax_state && <div className="cp-sub">{r.tax_state}{r.tax_mode === 'rate' ? ` · ${r.tax_rate}% tax` : ''}</div>}</td>
                  <td style={R}>{money(r.net_sales)}</td>
                  <td style={R}>{Number(r.cash_collected) ? money(r.cash_collected) : '—'}</td>
                  <td style={R}>{Number(r.tax_deducted) ? '−' + money(r.tax_deducted) : '—'}</td>
                  <td style={R}>−{money(r.card_fees)}</td>
                  <td style={R}>−{money(r.service_fee)}</td>
                  <td style={{ ...R, fontWeight: 700 }}>{money(r.amount_due)}
                    <div className="cp-sub" style={{ fontWeight: 400 }}>{r.credit_note ? 'Credited' : (Number(r.amount_due) > 0 ? 'Credit pending' : '—')}</div></td>
                </tr>
                {open === r.report_date && (
                  <tr><td colSpan={7} style={{ background: 'var(--cp-bg)' }}>
                    {r.credit_note && <div className="cp-sub" style={{ marginBottom: 10 }}>Credit note {r.credit_note}</div>}
                    <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                      <div>
                        <div style={LINE}><span>Gross sales</span><span style={V}>{money(r.gross_sales)}</span></div>
                        {Number(r.returns) > 0 && <div style={LINE}><span>Returns</span><span style={V}>−{money(r.returns)}</span></div>}
                        {Number(r.discounts) > 0 && <div style={LINE}><span>Discounts &amp; comps</span><span style={V}>−{money(r.discounts)}</span></div>}
                        <div style={LINE}><span>Net sales</span><span style={V}>{money(r.net_sales)}</span></div>
                        {Number(r.service_charges) > 0 && <div style={LINE}><span>Service charges</span><span style={V}>{money(r.service_charges)}</span></div>}
                        {Number(r.square_tax) > 0 && <div style={LINE}><span>Sales tax collected</span><span style={V}>{money(r.square_tax)}</span></div>}
                        <div style={LINE}><span>Tips (100% yours)</span><span style={V}>{money(r.tips)}</span></div>
                        <div style={{ ...LINE, borderTop: '1px solid var(--cp-line)' }}><span>Total collected</span><span style={V}>{money(r.collected)}</span></div>
                        {Number(r.cash_collected) !== 0 && <div style={LINE}><span>Cash — kept by you</span><span style={V}>{money(r.cash_collected)}</span></div>}
                        <div style={LINE}><span>Card — collected by XYZ</span><span style={V}>{money(r.card_collected)}</span></div>
                      </div>
                      <div>
                        <div style={LINE}><span>Card payments collected by XYZ</span><span style={V}>{money(r.card_collected)}</span></div>
                        {r.tax_mode === 'rate' && <div style={LINE}><span>Sales tax on all sales{Number(r.cash_collected) ? ' incl. cash' : ''}, {r.tax_state} {r.tax_rate}% (filed by XYZ)</span><span style={V}>−{money(r.tax_deducted)}</span></div>}
                        {r.tax_mode === 'square' && <div style={LINE}><span>Sales tax on all sales (filed by XYZ)</span><span style={V}>−{money(r.tax_deducted)}</span></div>}
                        <div style={LINE}><span>Card processing fees (Square, at cost)</span><span style={V}>−{money(r.card_fees)}</span></div>
                        <div style={LINE}><span>Event service fee {r.service_fee_pct}% of card sales {money(r.service_fee_base)}</span><span style={V}>−{money(r.service_fee)}</span></div>
                        <div style={{ ...LINE, borderTop: '1px solid var(--cp-line)', fontWeight: 700 }}><span>Due to you</span><span style={V}>{money(r.amount_due)}</span></div>
                        {r.fee_detail && <div className="cp-sub">Square rates: {r.fee_detail}</div>}
                      </div>
                      {(r.top_items.length > 0 || r.items_sold != null) && (
                        <div>
                          <div className="cp-k" style={{ marginBottom: 6 }}>{r.items_sold != null ? `${r.items_sold} items sold` : 'Top sellers'}{r.customers != null ? ` · ${r.customers} customers` : ''}</div>
                          {r.top_items.slice(0, 10).map((x) => <div key={x.name} style={{ ...LINE, fontSize: 13 }}><span>{x.name} × {x.qty}</span><span style={V}>{money(x.sales)}</span></div>)}
                        </div>
                      )}
                    </div>
                    {r.note && <div className="cp-sub" style={{ marginTop: 10, fontSize: 13 }}>{r.note}</div>}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {d.reports.length > 0 && (
              <tr style={{ fontWeight: 700 }}>
                <td>Total</td><td style={R}>{money(t.net_sales)}</td><td style={R}>{Number(t.cash_collected) ? money(t.cash_collected) : '—'}</td>
                <td style={R}>{Number(t.tax_deducted) ? '−' + money(t.tax_deducted) : '—'}</td><td style={R}>−{money(t.card_fees)}</td>
                <td style={R}>−{money(t.service_fee)}</td><td style={R}>{money(t.amount_due)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="cp-sub" style={{ padding: '0 20px 14px' }}>Tap a row for the full breakdown. Cash sales stay with you; sales tax on them is filed by XYZ and deducted from the card money. Each report is credited to your account by credit note; see Account Statement.</div>
      {s && (
        <div style={{ borderTop: '1px solid var(--cp-line)', padding: '16px 20px', display: 'grid', gap: 4, maxWidth: 560 }}>
          <div className="cp-k" style={{ marginBottom: 6 }}>Settlement</div>
          <div style={LINE}><span>Due to you from POS sales</span><span style={V}>{money(t.amount_due)}</span></div>
          {s.lines.map((l, i) => <div key={i} style={LINE}><span>{l.label}</span><span style={V}>−{money(l.amount)}</span></div>)}
          <div style={{ ...LINE, borderTop: '1px solid var(--cp-line)', fontWeight: 700 }}><span>Net settlement</span><span style={V}>{money(s.net)}</span></div>
          {s.invoice_number && <div className="cp-sub">Lease charges are on invoice {s.invoice_number}.</div>}
          {s.note && <div className="cp-sub" style={{ fontSize: 13 }}>{s.note}</div>}
        </div>
      )}
    </div>
  );
}
